import React, { useEffect, useState } from "react";
import CheckoutPageComponent from "../../components/CheckoutComponent/CheckoutComponent";
import CheckoutPage, { LoadingOverlay, LoadingSpinner } from "./CheckoutAll";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_PAYMENT_LINK_BY_SHORTCODE } from "../../graphql/queries/paymentlink";
import { environment } from "../../env";
import {
  checkoutProduct,
  selectedProduct,
  brandSettings as $brandSettings,
} from "../../store/nanostore";
import { useStore } from "@nanostores/react";
import ErrorComponent from "../../components/NotFound";
import useBrandSettings from "../../utils/getBrandSettingsByOrganizationId";
import { ChevronDown, ChevronUp } from "lucide-react";

const Checkout = () => {
  const { id } = useParams();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { loading, error, data, refetch } = useQuery(
    GET_PAYMENT_LINK_BY_SHORTCODE,
    {
      variables: { ...environment.paymentLinkService, id: id },
      onError() {},
    }
  );

  const organizationId =
    data?.paymentLinkByShortcode?.paymentLink?.organizationId;
  const transactionStatus = data?.paymentLinkByShortcode?.transaction?.status;

  const {
    brandSettings,
    loading: brandLoading,
    error: brandError,
  } = useBrandSettings(organizationId);

  useEffect(() => {
    if (!loading && data) {
      checkoutProduct.set(data?.paymentLinkByShortcode);
      selectedProduct.set(
        data?.paymentLinkByShortcode?.products?.map(
          ({ productId, quantity, ProductInstance, paymentLinkId }: any) => ({
            ProductInstance,
            id: productId,
            productId,
            paymentLinkId,
            quantity,
            price: ProductInstance.price,
          })
        )
      );
    }
  }, [loading, data]);

  useEffect(() => {
    if (!brandLoading && brandSettings) {
      $brandSettings.set(brandSettings);
    }
  }, [brandSettings, brandLoading]);

  useEffect(() => {
    const checkScrollable = () => {
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      setShowScrollIndicator(documentHeight > windowHeight);
    };

    const checkScrollPosition = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Check if scrolled to bottom with a small buffer
      setIsScrolledToBottom(
        Math.abs(scrollHeight - scrollTop - clientHeight) < 10
      );
    };

    checkScrollable();
    checkScrollPosition();

    window.addEventListener("resize", checkScrollable);
    window.addEventListener("scroll", checkScrollPosition);

    return () => {
      window.removeEventListener("resize", checkScrollable);
      window.removeEventListener("scroll", checkScrollPosition);
    };
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (transactionStatus === "pending") {
      interval = setInterval(() => {
        refetch();
      }, 3000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [transactionStatus, refetch]);

  const scrollHandler = () => {
    if (isScrolledToBottom) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return loading || brandLoading ? (
    <LoadingOverlay>
      <LoadingSpinner />
    </LoadingOverlay>
  ) : !error || !brandError ? (
    <Wrapper>
      <CheckoutPageComponent />
      <CheckoutPage />
      {showScrollIndicator && (
        <ScrollIndicator onClick={scrollHandler}>
          {isScrolledToBottom ? (
            <ChevronUp size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
        </ScrollIndicator>
      )}
    </Wrapper>
  ) : (
    <ErrorComponent />
  );
};

export default Checkout;

export const ScrollIndicator = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #00a603;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  flex-direction: row;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-bottom: 60px; /* Add space for the scroll indicator */
  }
`;
